import { useCallback, useEffect, useState } from 'react';

import { usePlayerContext } from '../../player/PlayerContext';
import { usePlayerEvent } from '../utils/usePlayerEvent';

type SvpChaptersEntity = {
  timeline: number;
  title: string;
  description: string | null;
  featured: boolean;
};

/**
 * @private
 * Exported for tests only.
 * @param {number} currentTime - in seconds
 * @param {SvpChaptersEntity[]} chapters - sorted ascending by timeline
 * @return {null|number} - active chapter array index (null if no chapter is matched)
 */
function mapTimeToChapter(currentTime: number, chapters: SvpChaptersEntity[]) {
  let activeIndex = null;

  // iterate starting from the last chapter - break when the first matching chapter is found
  for (let index = chapters.length - 1; index >= 0; index--) {
    const chapter = chapters[index];

    if (currentTime >= chapter.timeline) {
      activeIndex = index;
      break;
    }
  }

  return activeIndex;
}

/**
 * @param {SvpChaptersEntity[]} chapters
 */
function usePlayerChapters(chapters: SvpChaptersEntity[]) {
  const [activeChapterIndex, setActiveChapterIndex] = useState<number | null>(
    null
  );
  const { seekToTime } = usePlayerContext();

  // reset the state on asset change
  useEffect(() => {
    setActiveChapterIndex(null);
  }, [chapters]);

  const onTimeChange = useCallback(
    (currentTime) => {
      setActiveChapterIndex(mapTimeToChapter(currentTime, chapters));
    },
    [chapters]
  );

  usePlayerEvent('time', onTimeChange);

  const seekToChapter = useCallback(
    /**
     * @param {SvpChaptersEntity} chapter
     */
    function (chapter) {
      seekToTime(chapter.timeline);
    },
    [seekToTime]
  );

  return { activeChapterIndex, seekToChapter };
}

export { usePlayerChapters, mapTimeToChapter };
